import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../contexts/AuthContext';
import { setAuthToken } from '../services/api';

const Login = () => {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setAuthToken(user.token);
      navigate('/');
    }
  }, [user, navigate]);

  // Check for redirect result on mount
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        // This will be handled by AuthContext, but we check here too
        if (user && user.token) {
          setAuthToken(user.token);
          navigate('/');
        }
      } catch (error) {
      }
    };
    checkRedirectResult();
  }, [user, navigate]);

  const handleLogin = async () => {
    try {
      const userData = await login(false);
      
      // If userData is null (popup cancelled), don't show error
      if (userData === null) {
        return; // User cancelled, silently return
      }
      
      if (userData && userData.token) {
        setAuthToken(userData.token);
        navigate('/');
      }
    } catch (error) {
      
      // Show user-friendly error message
      if (error.message && error.message.includes('Popup blocked')) {
        alert('Popup was blocked. Please allow popups for this site.');
      } else if (error.code !== 'auth/cancelled-popup-request' && error.code !== 'auth/popup-closed-by-user') {
        alert(error.message || 'Login failed. Please try again.');
      }
      // If it's a cancellation, don't show an error
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const subscriptionPlans = [
    {
      name: 'Free',
      price: '$0',
      period: 'Forever',
      features: [
        'Up to 10 family members',
        'Up to 5 events',
        'Up to 3 wishlists',
        'Basic event management',
        'Wishlist sharing',
        'Name picker',
        'Recipe sharing',
        'Basic customization',
      ],
      popular: false,
    },
    {
      name: 'Premium',
      price: '$5.99',
      originalPrice: '$9.99',
      period: 'per month',
      isPromotional: true,
      features: [
        'Up to 50 family members',
        'Unlimited events',
        'Unlimited wishlists',
        'Advanced customization',
        'Background images',
        'Priority support',
        'All free features',
      ],
      popular: true,
    },
    {
      name: 'Extended Family',
      price: '$29.99',
      period: 'per month',
      features: [
        'Unlimited family members',
        'Unlimited events',
        'Unlimited wishlists',
        'Advanced analytics',
        'Custom branding',
        'Dedicated support',
        'All premium features',
      ],
      popular: false,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundImage: 'url("/Jubilea-Log-In.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          minHeight="100vh"
        >
          {/* Login Card */}
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              width: '100%',
              maxWidth: 500,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              mb: 4,
            }}
          >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <img 
              src="/Logo.png"
              alt="Jubilea" 
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
              style={{ 
                maxWidth: '200px',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
            <Typography
              variant="h3"
              component="span"
              sx={{ 
                display: 'none',
                fontWeight: 700, 
                color: '#5ce1e6',
              }}
            >
              Jubilea
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your all-in-one family celebration management platform
          </Typography>
          
          {/* Login Buttons - Moved to Top */}
          <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{ 
                px: 4, 
                py: 1.5,
                mb: 2,
                backgroundColor: '#5ce1e6',
                '&:hover': {
                  backgroundColor: '#4dd1d6',
                },
              }}
            >
              Get Started Free
            </Button>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="outlined"
                size="medium"
                onClick={handleLogin}
                sx={{ 
                  px: 3, 
                  py: 1,
                  borderColor: '#5ce1e6',
                  color: '#5ce1e6',
                  '&:hover': {
                    borderColor: '#4dd1d6',
                    backgroundColor: 'rgba(92, 225, 230, 0.1)',
                  },
                }}
              >
                Sign in with Google
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              New users will automatically create an account
            </Typography>
          </Box>
        </Paper>

        {/* App Features */}
        <Box sx={{ width: '100%', mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Everything You Need for Family Celebrations
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 800, mx: 'auto', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            Jubilea helps families organize events, manage wishlists, coordinate gift-giving, 
            and stay connected.
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>📅 Events</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Plan and manage family events with RSVPs, food sign-ups, and more
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>🎁 Wishlists</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create and share wishlists with family members
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>🎲 Name Picker</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organize gift exchanges and secret Santa events
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>🍽️ Recipes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Share and organize family recipes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Subscription Plans */}
        <Box sx={{ width: '100%' }}>
          <Typography variant="h4" sx={{ mb: 1, textAlign: 'center', fontWeight: 600, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Choose Your Plan
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: 'center', color: 'error.main', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', fontWeight: 500 }}>
            Family-Level Subscription • One subscription for the whole family
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'error.main', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', fontWeight: 500 }}>
            Start free, upgrade anytime
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {subscriptionPlans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} key={plan.name}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    border: plan.popular ? 2 : 1,
                    borderColor: plan.popular ? 'primary.main' : 'divider',
                    boxShadow: plan.popular ? 4 : 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)',
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        backgroundColor: '#5ce1e6',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <CardContent sx={{ pt: plan.popular ? 6 : 3 }}>
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                      {plan.name}
                    </Typography>
                    {plan.isPromotional && (
                      <Chip
                        label="Limited Time Offer"
                        color="error"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                        {plan.originalPrice && (
                          <Typography
                            variant="h6"
                            component="span"
                            sx={{
                              textDecoration: 'line-through',
                              color: 'text.secondary',
                              fontWeight: 400,
                            }}
                          >
                            {plan.originalPrice}
                          </Typography>
                        )}
                        <Typography variant="h4" component="span" sx={{ fontWeight: 700, color: plan.isPromotional ? 'error.main' : 'inherit' }}>
                          {plan.price}
                        </Typography>
                        {plan.period !== 'Forever' && (
                          <Typography variant="body2" color="text.secondary" component="span">
                            {' '}{plan.period}
                          </Typography>
                        )}
                      </Box>
                      {plan.isPromotional && (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                          Save {plan.originalPrice && `$${(parseFloat(plan.originalPrice.replace('$', '')) - parseFloat(plan.price.replace('$', ''))).toFixed(2)}`} per month!
                        </Typography>
                      )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <List dense>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
